import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  components,
  InputActionMeta,
  SingleValue,
  SingleValueProps,
} from 'react-select';
import debounce from 'lodash/debounce';
import { useLazyQuery } from '@apollo/client';
import { ReactSelectMenu } from '../../../Dropdown/StyledReactSelect';
import { GET_NAMED_COHORTS } from '../../../../utils/apollo/cohorts';
import {
  ICohortFieldsFragment,
  IGetNamedCohortsQuery,
  IGetNamedCohortsQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { activeSchemaIdSelector } from '../../../../redux/selectors/builder';
import { VersionedEntity } from '../../../EntityLabels';
import BuilderInfo from '../../../BuilderInfo';
import { theme } from '../../../../styles/theme';

interface IProps {
  onChange: (cohort: ICohortFieldsFragment) => void;
  onClear: () => void;
  includeCompound: boolean;
  isExpanded?: boolean;
  autoFocus?: boolean;
  defaultNamedCohort: ICohortFieldsFragment | null;
  isDisabled?: boolean;
}

export default ({
  onChange,
  onClear,
  isExpanded,
  includeCompound,
  autoFocus,
  defaultNamedCohort,
  isDisabled,
}: IProps) => {
  /* Track current cohort */
  const [currentCohort, setCurrentCohort] =
    useState<ICohortFieldsFragment | null>(defaultNamedCohort);
  const [options, setOptions] = useState<ICohortFieldsFragment[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const activeSchemaId = useSelector(activeSchemaIdSelector);

  const [loadNamedCohorts, { data, loading, called, variables }] = useLazyQuery<
    IGetNamedCohortsQuery,
    IGetNamedCohortsQueryVariables
  >(GET_NAMED_COHORTS, {
    variables: {
      term: searchTerm,
      schemaId: activeSchemaId,
      includes: [],
      includeCompound,
      pagination: { first: 100, after: 0 },
    },
  });

  const handleChange = (newOption: SingleValue<ICohortFieldsFragment>) => {
    setCurrentCohort(newOption);

    if (!newOption) {
      onClear();

      return;
    }

    onChange(newOption);
  };

  // This callback would be fired even when input hasn't changed
  const handleSearch = debounce((term: string, actionMeta: InputActionMeta) => {
    // Using actionMeta to detect cases to avoid refetch when:
    // 1. Input hasn't been changed and user opens-closes menu;
    // 2. Input hasn't been changed and user selects an option;
    if (actionMeta.action === 'menu-close' && !actionMeta.prevInputValue) {
      return;
    }

    setSearchTerm(term);
  }, 200);

  const renderLabel = (
    props: SingleValueProps<ICohortFieldsFragment, false>,
  ) => {
    const { data } = props;
    const version = data?.version;

    if (!version) {
      return (
        <components.SingleValue {...props}>
          {props.children}
        </components.SingleValue>
      );
    }

    const { name, isDisabled, isLatestVersion } = version;

    return (
      <components.SingleValue {...props}>
        <VersionedEntity
          onClear={props.clearValue}
          label={name}
          isDisabled={isDisabled}
          isLatestVersion={isLatestVersion}
        >
          <BuilderInfo
            kind="namedCohort"
            cohort={data}
            isExpanded={isExpanded}
            fetched={true}
          />
        </VersionedEntity>
      </components.SingleValue>
    );
  };

  const handleLoadOptions = () => {
    if (!called && !isDisabled) {
      loadNamedCohorts();
    }
  };

  // KB-6735: https://bitbucket.org/kubitai/caprica/pull-requests/3565
  useEffect(() => {
    if (!loading && searchTerm === variables?.term) {
      const entities = data?.namedCohorts?.entities;
      if (!entities?.length) {
        setOptions([]);
      } else {
        const sortedEntity = entities.sort((a, b) =>
          (a?.version?.name ?? '').localeCompare(b?.version?.name ?? ''),
        );
        setOptions(sortedEntity);
      }
    }
  }, [loading, data, variables, searchTerm]);

  return (
    <ReactSelectMenu<ICohortFieldsFragment>
      isDisabled={isDisabled}
      isMulti={false}
      options={options}
      value={currentCohort}
      onMenuOpen={handleLoadOptions}
      onChange={handleChange}
      onInputChange={handleSearch}
      components={{ SingleValue: renderLabel }}
      getOptionLabel={(option) => option?.version?.name ?? ''}
      getOptionValue={(option) => option.id}
      autoFocus={autoFocus}
      openMenuOnFocus={autoFocus}
      isLoading={loading}
      blurInputOnSelect
      hideSelectedOptions
      styles={{
        option: (base, props) => {
          if (props?.data?.__typename === 'CompoundCohort') {
            return {
              ...base,
              color: theme.colors.secondary900,
            };
          }

          return base;
        },
      }}
    />
  );
};
